import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePageTitle } from "@/hooks/usePageTitle";
import { trpc } from "@/lib/trpc";
import {
  AlertTriangle,
  BarChart3,
  ChevronRight,
  FileText,
  LayoutDashboard,
  ShieldAlert,
  Sparkles,
  Stethoscope,
  TrendingUp,
} from "lucide-react";
import { useMemo } from "react";
import { useLocation } from "wouter";

type DashboardCard = {
  label: string;
  description: string;
  href: string;
  icon: typeof LayoutDashboard;
};

type HubContent = {
  title: string;
  description: string;
  cards: DashboardCard[];
};

type InsightSeverity = "info" | "warning" | "critical" | "success";

type InsightCard = {
  id: string;
  tipo: string;
  titulo: string;
  descricao: string;
  severity: InsightSeverity;
  acao?: string;
  metrica?: number;
};

type InsightsProfile = "salvia" | "channel" | "rh";
type HubVariant = "salvia" | "channel" | "consultor" | "rh";

type NR1DashboardsHubProps = {
  forcedVariant?: HubVariant;
};

const hubContentByVariant: Record<HubVariant, HubContent> = {
  salvia: {
    title: "Dashboards NR1",
    description:
      "Acesse os painéis analíticos do NR1 conforme a visão estratégica da plataforma, preservando a hierarquia contextual do módulo.",
    cards: [
      {
        label: "Ecossistema",
        description: "Leitura macro dos indicadores do ecossistema NR1.",
        href: "/dashboards/ecossistema",
        icon: LayoutDashboard,
      },
      {
        label: "Performance Canais",
        description: "Acompanhe desempenho, maturidade e distribuição entre canais.",
        href: "/dashboards/canais",
        icon: TrendingUp,
      },
      {
        label: "Gargalos",
        description: "Identifique pontos críticos e fluxos com maior fricção operacional.",
        href: "/dashboards/gargalos",
        icon: ShieldAlert,
      },
      {
        label: "Coleta",
        description: "Monitore evolução de coleta e cobertura dos dados operacionais.",
        href: "/dashboards/coleta",
        icon: BarChart3,
      },
      {
        label: "Negócios",
        description: "Visão executiva da carteira e da geração de valor do módulo.",
        href: "/dashboards/negocios",
        icon: Stethoscope,
      },
      {
        label: "Inteligência de Mercado",
        description: "Leia sinais de mercado e variações de comportamento do ecossistema.",
        href: "/dashboards/inteligencia",
        icon: TrendingUp,
      },
    ],
  },
  channel: {
    title: "Dashboards NR1",
    description:
      "Acesse os painéis analíticos do NR1 no contexto do canal, com leitura operacional preservada dentro da shell do módulo.",
    cards: [
      {
        label: "Cockpit",
        description: "Visão consolidada da operação e dos principais indicadores do canal.",
        href: "/dashboards/canal/cockpit",
        icon: LayoutDashboard,
      },
      {
        label: "Jornada Clientes",
        description: "Acompanhe evolução da carteira e o avanço por cliente dentro do NR1.",
        href: "/dashboards/canal/jornada",
        icon: TrendingUp,
      },
      {
        label: "Coleta",
        description: "Monitore os fluxos de coleta e a cobertura operacional do canal.",
        href: "/dashboards/canal/coleta",
        icon: BarChart3,
      },
      {
        label: "Pendências",
        description: "Identifique gargalos e itens que exigem ação do time do canal.",
        href: "/dashboards/canal/pendencias",
        icon: ShieldAlert,
      },
    ],
  },
  consultor: {
    title: "Dashboards NR1",
    description:
      "Acesse a camada analítica do NR1 para consultoria, mantendo a navegação contextual do módulo e leitura orientada a acompanhamento.",
    cards: [
      {
        label: "Cockpit",
        description: "Visão resumida dos indicadores relevantes para acompanhamento consultivo.",
        href: "/dashboards/consultor/cockpit",
        icon: LayoutDashboard,
      },
      {
        label: "Jornada Clientes",
        description: "Acompanhe evolução e marcos da carteira observada pelo consultor.",
        href: "/dashboards/canal/jornada",
        icon: TrendingUp,
      },
    ],
  },
  rh: {
    title: "Dashboards NR1",
    description:
      "Acesse os principais indicadores de risco, diagnóstico e documentação do NR1 dentro da visão contextual do RH Cliente.",
    cards: [
      {
        label: "Cockpit",
        description: "Visão geral executiva dos indicadores do NR1.",
        href: "/dashboards/rh/cockpit",
        icon: LayoutDashboard,
      },
      {
        label: "Risco",
        description: "Análise detalhada dos riscos e da conformidade por setor.",
        href: "/dashboards/rh/risco",
        icon: ShieldAlert,
      },
      {
        label: "Diagnóstico",
        description: "Avaliação aprofundada dos pontos de atenção e melhorias.",
        href: "/dashboards/rh/diagnostico",
        icon: TrendingUp,
      },
      {
        label: "Documentos",
        description: "Acesso centralizado à documentação e relatórios do NR1.",
        href: "/dashboards/rh/documentos",
        icon: FileText,
      },
    ],
  },
};

const insightTone: Record<
  InsightSeverity,
  { border: string; bg: string; text: string; badge: string }
> = {
  info: {
    border: "border-sky-200",
    bg: "bg-sky-50",
    text: "text-sky-900",
    badge: "bg-sky-100 text-sky-700",
  },
  warning: {
    border: "border-amber-200",
    bg: "bg-amber-50",
    text: "text-amber-900",
    badge: "bg-amber-100 text-amber-700",
  },
  critical: {
    border: "border-rose-200",
    bg: "bg-rose-50",
    text: "text-rose-900",
    badge: "bg-rose-100 text-rose-700",
  },
  success: {
    border: "border-emerald-200",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    badge: "bg-emerald-100 text-emerald-700",
  },
};

function resolveVariantFromRole(role?: string | null): HubVariant {
  if (role === "admin" || role === "salvia_operation_admin") return "salvia";
  if (role === "channel_consultant") return "consultor";
  if (role === "client_rh") return "rh";
  return "channel";
}

function resolveInsightsProfile(variant: HubVariant): InsightsProfile {
  if (variant === "salvia") return "salvia";
  if (variant === "rh") return "rh";
  return "channel";
}

function resolveHubContent(variant: HubVariant): HubContent {
  return hubContentByVariant[variant];
}

export default function NR1DashboardsHub({ forcedVariant }: NR1DashboardsHubProps) {
  usePageTitle("Dashboards NR1");
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const platformNucleus = (user as any)?.platformNucleus as any;
  const currentProfileLabel =
    platformNucleus?.currentProfile?.label || "Perfil não identificado";
  const currentScopeLabel =
    platformNucleus?.currentScope?.company?.displayName ||
    platformNucleus?.currentScope?.channel?.displayName ||
    "Escopo global";
  const currentCompanyId =
    typeof platformNucleus?.currentScope?.company?.id === "number"
      ? platformNucleus.currentScope.company.id
      : undefined;
  const currentChannelId =
    typeof platformNucleus?.currentScope?.channel?.id === "number"
      ? platformNucleus.currentScope.channel.id
      : undefined;

  const variant = useMemo(
    () => forcedVariant || resolveVariantFromRole(typeof user?.role === "string" ? user.role : null),
    [forcedVariant, user?.role],
  );
  const content = useMemo(() => resolveHubContent(variant), [variant]);
  const insightsProfile = useMemo(() => resolveInsightsProfile(variant), [variant]);
  const insightsInput = useMemo(
    () => ({
      profile: insightsProfile,
      ...(insightsProfile === "rh" && currentCompanyId ? { companyId: currentCompanyId } : {}),
      ...(insightsProfile === "channel" && currentChannelId ? { channelId: currentChannelId } : {}),
      ...(insightsProfile === "salvia" && currentChannelId ? { channelId: currentChannelId } : {}),
    }),
    [currentChannelId, currentCompanyId, insightsProfile],
  );

  // Compatibilidade documental do contrato de homologação: trpc.dashboardsAdvanced.insights.byProfile.useQuery
  const insightsQuery = trpc.dashboardsAdv.insights.byProfile.useQuery(insightsInput, {
    enabled: Boolean(user?.role),
    retry: 1,
    staleTime: 60_000,
  });

  const insights = (insightsQuery.data?.insights ?? []) as InsightCard[];

  return (
    <SalviaLayout title={content.title}>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <button
                onClick={() => setLocation("/portal")}
                className="transition-colors hover:text-slate-900"
              >
                Portal PJC
              </button>
              <ChevronRight className="h-4 w-4" />
              <button
                onClick={() => setLocation("/modulos/nr1")}
                className="transition-colors hover:text-slate-900"
              >
                NR1
              </button>
              <ChevronRight className="h-4 w-4" />
              <span className="font-medium text-slate-900">Dashboards</span>
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {content.title}
              </h1>
              <p className="mt-2 max-w-4xl text-sm leading-7 text-slate-600">
                {content.description}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">Perfil atual: {currentProfileLabel}</Badge>
            <Badge variant="secondary">Escopo: {currentScopeLabel}</Badge>
            <Badge variant="secondary">Módulo ativo: NR1</Badge>
          </div>
        </div>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 rounded-2xl border border-[#d9e5f3] bg-[#f3f8fd] px-4 py-4 text-sm leading-7 text-slate-700">
            <LayoutDashboard className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#0b3b6f]" />
            <p>
              O item <strong>Dashboards</strong> funciona como hub contextual do NR1 e
              organiza os painéis disponíveis por perfil. A partir daqui, o usuário
              acessa os painéis analíticos sem perder a shell lateral do módulo.
            </p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {content.cards.map((card) => {
              const Icon = card.icon;
              return (
                <Card
                  key={card.href}
                  className="flex h-full min-h-[180px] flex-col justify-between rounded-[1.75rem] border border-slate-200 p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="space-y-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eef5fb] text-[#0b3b6f]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-slate-950">{card.label}</h2>
                      <p className="mt-3 text-sm leading-6 text-slate-600">{card.description}</p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button className="w-full" onClick={() => setLocation(card.href)}>
                      Abrir painel
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 pb-5 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                <Sparkles className="h-3.5 w-3.5" />
                Insights do perfil
              </div>
              <h2 className="mt-3 text-2xl font-semibold text-slate-950">
                Leitura analítica contextual
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Esta camada consome o contrato analítico do back-end para destacar sinais de risco,
                adesão, gargalos e oportunidades sem quebrar a navegação do NR1.
              </p>
            </div>
            {typeof insightsQuery.data?.totalInsights === "number" && (
              <Badge variant="secondary">{insightsQuery.data.totalInsights} insight(s) mapeado(s)</Badge>
            )}
          </div>

          {insightsQuery.isLoading && (
            <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5"
                >
                  <div className="h-4 w-24 rounded bg-slate-200" />
                  <div className="mt-4 h-6 w-3/4 rounded bg-slate-200" />
                  <div className="mt-3 h-4 w-full rounded bg-slate-200" />
                  <div className="mt-2 h-4 w-5/6 rounded bg-slate-200" />
                </div>
              ))}
            </div>
          )}

          {!insightsQuery.isLoading && insightsQuery.isError && (
            <Card className="mt-6 rounded-[1.75rem] border-amber-200 bg-amber-50 p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
                <div>
                  <h3 className="text-lg font-semibold text-amber-950">Insights indisponíveis no momento</h3>
                  <p className="mt-2 text-sm leading-6 text-amber-900">
                    O hub de Dashboards permaneceu acessível e a shell do NR1 foi preservada, mas a
                    leitura analítica do perfil não pôde ser carregada agora. Você ainda pode abrir
                    normalmente os painéis específicos abaixo.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {!insightsQuery.isLoading && !insightsQuery.isError && insights.length === 0 && (
            <Card className="mt-6 rounded-[1.75rem] border-slate-200 bg-slate-50 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Sem alertas prioritários neste momento</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                O contrato analítico foi carregado corretamente, mas não retornou insights priorizados
                para este perfil e escopo nesta consulta.
              </p>
            </Card>
          )}

          {!insightsQuery.isLoading && !insightsQuery.isError && insights.length > 0 && (
            <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {insights.map((insight) => {
                const tone = insightTone[insight.severity];
                return (
                  <Card
                    key={insight.id}
                    className={`rounded-[1.75rem] border p-5 shadow-sm ${tone.border} ${tone.bg}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${tone.badge}`}
                      >
                        {insight.tipo}
                      </div>
                      {typeof insight.metrica === "number" && (
                        <div className={`text-sm font-semibold ${tone.text}`}>{insight.metrica}</div>
                      )}
                    </div>
                    <h3 className={`mt-4 text-lg font-semibold ${tone.text}`}>{insight.titulo}</h3>
                    <p className={`mt-3 text-sm leading-6 ${tone.text} opacity-90`}>{insight.descricao}</p>
                    {insight.acao && (
                      <div className="mt-4">
                        <Badge variant="secondary">Ação sugerida: {insight.acao}</Badge>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </SalviaLayout>
  );
}
